import { useAuthStore, useSnackbar } from '@sunrise-ui/primitives';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../constants/QueryKeys';
import { adminProfilesApi, useAuthMutation, useAuthQuery } from '../utils/api';
import { assertParameterIsNotUndefinedOrNull } from '../utils/error';

const { allUsersKey, adminProfileKey, bulkKey } = QueryKeys.admin;

export const useGetAllUsers = (enabled: boolean) =>
  useAuthQuery({
    queryFn: async () =>
      adminProfilesApi.listUsersProfilesGet().then((response) => response.data),
    queryKey: [allUsersKey],
    enabled,
  });

export const useGetUserAdminProfile = () => {
  const { currentUserId } = useAuthStore();

  return useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      const response = await adminProfilesApi.getUserByIdProfilesUserIdGet({
        userId: currentUserId,
      });
      return response.data;
    },
    queryKey: [adminProfileKey, currentUserId],
    enabled: Boolean(currentUserId),
    staleTime: 30000,
  });
};

/**
 * Hook to fetch admin profiles and emails for multiple user IDs using the bulk endpoint
 */
export const useBulkGetAdminProfilesAndEmails = (userIds?: string[]) => {
  const queryClient = useQueryClient();

  return useAuthQuery({
    queryKey: [adminProfileKey, bulkKey, userIds],
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(userIds, 'userIds');
      const data = await adminProfilesApi
        .bulkGetProfilesProfilesBulkGetPost({
          bulkGetUserInput: {
            user_ids: userIds,
          },
        })
        .then((response) => response.data);

      const emailMap = new Map<string, string | undefined>();

      for (const user of data) {
        queryClient.setQueryData([adminProfileKey, user.id], user);
        emailMap.set(user.id, user.email);
      }

      return {
        users: data,
        emailMap,
      };
    },
    enabled: Boolean(userIds && userIds.length > 0),
  });
};

/**
 * Hook to update the current user's phone number in Cerebro
 * Phone number is stored separately from profile data
 */
export const useUpdateUserPhoneNumber = () => {
  const { currentUserId } = useAuthStore();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    mutationFn: async (phoneNumber: string | undefined) => {
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      const response = await adminProfilesApi.updateUserProfilesPut({
        updateUserInput: {
          id: currentUserId,
          phone_number: phoneNumber,
        },
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [adminProfileKey, currentUserId],
      });
    },
    onError: () => {
      showSnackbar({
        text: 'Failed to update phone number. Please try again.',
        type: 'error',
      });
    },
  });
};
